/**
 * The questionnaire's mount for the Resolve chat widget.
 *
 * This adapter is deliberately thin: it reads the deployment's chat
 * configuration and renders the widget the chat-service repository owns. No
 * chat behavior is copied here. It passes two things from the questionnaire.
 * The first is the case id, as the questionnaire run the conversation belongs
 * to, so the two can be joined later in Data Hub. The second, and the one value
 * derived from the page, is a description of the current step built from that
 * step's static title and subtitle, so the assistant knows what the visitor is
 * looking at, followed by a rough note on how far the step is from an offer to
 * schedule a free consultation. A visitor's answers, their email, amounts computed from their
 * answers, and the Stytch session are never passed.
 *
 * The widget's usage events go to Google Tag Manager through `reportChatEvent`,
 * tagged with the current step's name.
 *
 * Chat shows only when the `fe_chat_enabled` Flagsmith flag is on in this
 * deployment's Flagsmith environment, which is how chat is turned on and off
 * everywhere. Until Flagsmith answers, or when it cannot be reached, it is off.
 *
 * A caller may pass `pageDescription` explicitly, and that description is sent
 * in place of the one built from the current step. It must never contain a
 * visitor answer, an identifier, or form state.
 */

import { ChatWidget } from '@resolve/chat-widget';
import '@resolve/chat-widget/styles.css';
import { useSyncExternalStore } from 'react';

import { FLAGSMITH_KEYS, useGetFlagValue } from '@/flagsmith';
import { queryClient } from '@/state/queryClient';
import type { CaseState } from '@/state/state.types';

import { readChatConfig, type ChatConfig } from './chatConfig';
import { describeStep } from './describeStep';
import { reportChatEvent } from './reportChatEvent';

// The chat mount sits outside the questionnaire's query provider, so it reads
// the case and its current step from the shared query client directly and
// re-renders when either changes. Only the step's id is read from the step.
const subscribeToCases = (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange);
const readCaseId = () => queryClient.getQueryData<CaseState>(['caseState'])?.case?.id;
const readStepId = () => queryClient.getQueryData<CaseState>(['caseState'])?.step?.id;

interface QuestionnaireChatProps {
  pageDescription?: string;
  config?: ChatConfig;
}

export const QuestionnaireChat = ({ pageDescription, config }: QuestionnaireChatProps) => {
  const chat = config ?? readChatConfig();
  const caseId = useSyncExternalStore(subscribeToCases, readCaseId);
  const stepId = useSyncExternalStore(subscribeToCases, readStepId);
  const flag = useGetFlagValue(FLAGSMITH_KEYS.CHAT);
  if (!(flag.enable && flag.value === true)) return null;
  return (
    <ChatWidget
      apiBasePath={chat.apiBasePath}
      enabled
      contact={chat.contact}
      pageDescription={pageDescription ?? describeStep(stepId)}
      questionnaireRunId={caseId}
      onEvent={(event) => reportChatEvent(event, stepId)}
    />
  );
};
